import { io, Socket } from "socket.io-client";
import { QueryClient } from "@tanstack/react-query";
import { BaseNotification } from "./types/notifications";
import { TokenStorage } from "./storage";
import toast from "react-hot-toast";

// Definiamo le stesse query key usate negli hook
const NOTIFICATIONS_QUERY_KEY = "notifications";
const MESSAGES_QUERY_KEY = "messages";
const UNREAD_COUNT_QUERY_KEY = "unread-count";

export class SocketClient {
  private static instance: SocketClient;
  private socket: Socket | null = null;
  private currentToken: string | null = null;
  private queryClient: QueryClient | null = null;
  private readonly API_URL = import.meta.env.VITE_API_URL;

  private constructor() {}

  public static getInstance(): SocketClient {
    if (!SocketClient.instance) {
      SocketClient.instance = new SocketClient();
    }
    return SocketClient.instance;
  }

  public setQueryClient(queryClient: QueryClient) {
    this.queryClient = queryClient;
  }

  public connect(token: string | null): void {
    if (!token) {
      console.warn("Socket connection attempted without token");
      return;
    }

    if (this.socket?.connected && this.currentToken === token) {
      return;
    }

    if (this.currentToken !== token && this.socket?.connected) {
      this.disconnect();
    }

    this.currentToken = token;

    try {
      console.log("Connecting socket with token...");
      this.socket = io(this.API_URL, {
        auth: {
          token: `Bearer ${token}`,
        },
        extraHeaders: {
          Authorization: `Bearer ${token}`,
        },
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
      });

      this.setupEventListeners();
    } catch (error) {
      console.error("Error connecting socket:", error);
    }
  }

  public disconnect(): void {
    if (this.socket) {
      console.log("Disconnecting socket");
      this.socket.disconnect();
      this.socket = null;
      this.currentToken = null;
    }
  }

  private setupEventListeners(): void {
    if (!this.socket) return;

    this.socket.on("connect", () => {
      console.log("Socket connected successfully");
    });

    this.socket.on("connected", (data) => {
      console.log("Server confirmed connection:", data);
    });

    this.socket.on("notification", (notification: BaseNotification) => {
      console.log("🔔 Received notification:", notification);

      if (this.queryClient) {
        // Invalidiamo tutte le query relative alle notifiche
        this.queryClient.invalidateQueries({
          queryKey: [NOTIFICATIONS_QUERY_KEY],
        });
        this.queryClient.invalidateQueries({ queryKey: [MESSAGES_QUERY_KEY] });
        this.queryClient.invalidateQueries({
          queryKey: [UNREAD_COUNT_QUERY_KEY],
        });
      }

      // Se la notifica richiede visualizzazione forzata
      if (notification.forceDisplay) {
        try {
          console.log("📢 Dispatching heavy_notification_received event");
          const event = new CustomEvent("heavy_notification_received", {
            detail: notification,
          });
          window.dispatchEvent(event);
          console.log("✅ Event dispatched successfully");
        } catch (error) {
          console.error("❌ Error dispatching event:", error);
        }
      }
    });

    this.socket.on("disconnect", () => {
      console.log("Socket disconnected");
    });

    this.socket.on("connect_error", (error) => {
      console.error("Connection error:", error);
    });

    this.socket.on("error", (error: Error) => {
      console.error("Socket error occurred:", error);
    });
  }
}
